import { useState, useEffect } from "react"
import { useDataStore } from "../stores/dataStore"
import { Search, Package, Shirt, Calendar, AlertTriangle, CheckCircle, User } from "lucide-react"
import "./GuardTracking.css"

export default function GuardTracking() {
  const { guards, fetchGuards, uniforms, fetchUniforms, assets, fetchAssets } = useDataStore()
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedGuard, setSelectedGuard] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      await Promise.all([fetchGuards(), fetchUniforms(), fetchAssets()])
      setLoading(false)
    }
    load()
  }, [])

  const getGuardItems = (guardName) => {
    const guardUniforms = uniforms.filter(u => 
      u.guardName?.toLowerCase() === guardName?.toLowerCase() ||
      u.guardName?.toLowerCase().includes(guardName?.toLowerCase())
    )
    const guardAssets = assets.filter(a => 
      a.assignedTo?.toLowerCase() === guardName?.toLowerCase() ||
      a.assignedTo?.toLowerCase().includes(guardName?.toLowerCase())
    )
    return { uniforms: guardUniforms, assets: guardAssets }
  }

  const getFrequencyStats = (guardName) => {
    const { uniforms: gu, assets: ga } = getGuardItems(guardName)
    const uniformFreq = {}
    gu.forEach(u => {
      const type = u.uniformType || "Unknown"
      uniformFreq[type] = (uniformFreq[type] || 0) + 1
    })
    const assetFreq = {}
    ga.forEach(a => {
      const type = a.assetType || a.name || "Unknown"
      assetFreq[type] = (assetFreq[type] || 0) + 1
    })
    return { uniformFreq, assetFreq, totalUniforms: gu.length, totalAssets: ga.length }
  }

  const detectMisuse = (guardName) => {
    const stats = getFrequencyStats(guardName)
    const alerts = []
    Object.entries(stats.uniformFreq).forEach(([type, count]) => {
      if (count > 2) alerts.push({ type: "uniform", item: type, count, severity: count > 3 ? "high" : "medium" })
    })
    Object.entries(stats.assetFreq).forEach(([type, count]) => {
      if (count > 2) alerts.push({ type: "asset", item: type, count, severity: count > 3 ? "high" : "medium" })
    })
    return alerts
  }

  const filteredGuards = guards.filter(g => {
    const name = `${g.firstName} ${g.lastName}`.toLowerCase()
    return !searchTerm || name.includes(searchTerm.toLowerCase()) || g.guardId?.toLowerCase().includes(searchTerm.toLowerCase())
  })

  return (
    <div className="guard-tracking-container">
      <div className="tracking-header">
        <h2><Package size={24} /> Guard Asset & Uniform Tracking</h2>
        <p>Monitor equipment assignments and detect potential misuse</p>
      </div>

      <div className="tracking-stats">
        <div className="stat-card"><User size={20} /><div><h3>{guards.length}</h3><p>Total Guards</p></div></div>
        <div className="stat-card"><Shirt size={20} /><div><h3>{uniforms.length}</h3><p>Uniforms Issued</p></div></div>
        <div className="stat-card"><Package size={20} /><div><h3>{assets.length}</h3><p>Assets Assigned</p></div></div>
        <div className="stat-card alert"><AlertTriangle size={20} /><div><h3>{filteredGuards.filter(g => detectMisuse(`${g.firstName} ${g.lastName}`).length > 0).length}</h3><p>Misuse Alerts</p></div></div>
      </div>

      <div className="tracking-filters">
        <div className="search-box"><Search size={18} /><input type="text" placeholder="Search guard..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      </div>

      {loading && <div className="loading">Loading...</div>}

      <div className="guards-table">
        <table>
          <thead><tr><th>Guard</th><th>ID</th><th>Uniforms</th><th>Assets</th><th>Alerts</th><th>Actions</th></tr></thead>
          <tbody>
            {filteredGuards.map(guard => {
              const guardName = `${guard.firstName} ${guard.lastName}`
              const stats = getFrequencyStats(guardName)
              const alerts = detectMisuse(guardName)
              return (
                <tr key={guard._id} className={alerts.length > 0 ? "has-alert" : ""}>
                  <td><div className="guard-cell"><div className="guard-avatar">{guard.firstName?.[0]}{guard.lastName?.[0]}</div><div><div className="guard-name">{guardName}</div><div className="guard-site">{guard.siteAssigned || "No site"}</div></div></div></td>
                  <td>{guard.guardId || "N/A"}</td>
                  <td><span className="badge uniform">{stats.totalUniforms}</span></td>
                  <td><span className="badge asset">{stats.totalAssets}</span></td>
                  <td>{alerts.length > 0 ? <span className={`alert-badge ${alerts[0].severity}`}><AlertTriangle size={14} /> {alerts.length}</span> : <span className="clean-badge"><CheckCircle size={14} /> Clean</span>}</td>
                  <td><button className="btn-view" onClick={() => { setSelectedGuard(guard); setShowDetails(true); }}>View</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showDetails && selectedGuard && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="modal-content modal-lg">
            <div className="modal-header"><h3>{selectedGuard.firstName} {selectedGuard.lastName} - History</h3><button className="btn-close" onClick={() => setShowDetails(false)}>X</button></div>
            <div className="modal-body">
              {(() => {
                const guardName = `${selectedGuard.firstName} ${selectedGuard.lastName}`
                const items = getGuardItems(guardName)
                const stats = getFrequencyStats(guardName)
                const alerts = detectMisuse(guardName)
                return (
                  <>
                    {alerts.length > 0 && <div className="alerts-section"><h4><AlertTriangle size={18} /> Alerts</h4>{alerts.map((a, i) => <div key={i} className={`alert-item ${a.severity}`}><strong>{a.item}</strong>: {a.count}x {a.severity === "high" ? "- Frequent replacement!" : ""}</div>)}</div>}
                    <div className="frequency-section"><h4><Calendar size={18} /> Uniform Frequency</h4>{Object.entries(stats.uniformFreq).length === 0 ? <p className="empty">None</p> : <div className="freq-grid">{Object.entries(stats.uniformFreq).map(([t, c]) => <div key={t} className={`freq-item ${c > 2 ? "high-freq" : ""}`}><span>{t}</span><span>{c}x</span></div>)}</div>}</div>
                    <div className="frequency-section"><h4><Package size={18} /> Asset Frequency</h4>{Object.entries(stats.assetFreq).length === 0 ? <p className="empty">None</p> : <div className="freq-grid">{Object.entries(stats.assetFreq).map(([t, c]) => <div key={t} className={`freq-item ${c > 2 ? "high-freq" : ""}`}><span>{t}</span><span>{c}x</span></div>)}</div>}</div>
                    <div className="items-list"><h4>All Items</h4><table className="items-table"><thead><tr><th>Type</th><th>Item</th><th>Date</th><th>Condition</th></tr></thead><tbody>{items.uniforms.map((u, i) => <tr key={`u-${i}`}><td><span className="type-badge uniform">Uniform</span></td><td>{u.uniformType || "N/A"}</td><td>{u.issueDate ? new Date(u.issueDate).toLocaleDateString() : "N/A"}</td><td>{u.condition || "N/A"}</td></tr>)}{items.assets.map((a, i) => <tr key={`a-${i}`}><td><span className="type-badge asset">Asset</span></td><td>{a.name || a.assetType || "N/A"}</td><td>{a.issueDate ? new Date(a.issueDate).toLocaleDateString() : "N/A"}</td><td>{a.condition || "N/A"}</td></tr>)}</tbody></table></div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
